import React, { useState, useEffect } from 'react';
import { Tag, Plus, Clock, Percent, ChevronDown, ChevronUp, X, DollarSign } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

function CountdownTimer({ endDate }) {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    const calc = () => {
      const diff = new Date(endDate) - new Date();
      if (diff <= 0) { setTimeLeft('Expired'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(d > 0 ? `${d}d ${h}h left` : `${h}h ${m}m left`);
    };
    calc();
    const t = setInterval(calc, 60000);
    return () => clearInterval(t);
  }, [endDate]);
  return <span style={{ color: '#F59E0B', fontFamily: 'DM Sans, sans-serif' }} className="text-xs font-medium">{timeLeft}</span>;
}

export default function PricingManagement({ partnerId }) {
  const [products, setProducts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkDiscount, setBulkDiscount] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoForm, setPromoForm] = useState({ product_id: '', promotion_name: '', discount_percent: '', start_date: '', end_date: '' });
  const [promoLoading, setPromoLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [priceEdits, setPriceEdits] = useState({});
  const [savingRow, setSavingRow] = useState(null);

  const fetchData = async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const [prodRes, promoRes, histRes] = await Promise.all([
        supabase?.from('retail_products')?.select('id, product_name, price, sale_price, promotional_price, category')?.eq('partner_id', partnerId)?.order('product_name'),
        supabase?.from('promotions')?.select('*, retail_products(product_name)')?.eq('partner_id', partnerId)?.order('created_at', { ascending: false }),
        supabase?.from('price_history')?.select('*, retail_products(product_name)')?.eq('partner_id', partnerId)?.order('changed_at', { ascending: false })?.limit(30),
      ]);
      setProducts(prodRes?.data || []);
      setPromotions(promoRes?.data || []);
      setPriceHistory(histRes?.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [partnerId]);

  const handleSaveRow = async (productId) => {
    const edits = priceEdits?.[productId] || {};
    if (!Object.keys(edits)?.length) return;
    setSavingRow(productId);
    try {
      const product = products?.find(p => p?.id === productId);
      const updates = {};
      for (const [field, value] of Object.entries(edits)) {
        const newVal = value === '' ? null : parseFloat(value);
        updates[field] = newVal;
        if (field === 'price' && product?.price !== newVal) {
          await supabase?.from('price_history')?.insert({ product_id: productId, partner_id: partnerId, old_price: product?.price, new_price: newVal, change_reason: 'Manual update' });
        }
      }
      await supabase?.from('retail_products')?.update(updates)?.eq('id', productId);
      setProducts(p => p?.map(x => x?.id === productId ? { ...x, ...updates } : x));
      setPriceEdits(e => { const n = { ...e }; delete n?.[productId]; return n; });
      setMsg({ type: 'success', text: 'Prices updated!' });
      fetchData();
    } catch (e) { setMsg({ type: 'error', text: 'Failed to save' }); } finally { setSavingRow(null); }
  };

  const handleBulkDiscount = async () => {
    if (!selectedProducts?.length || !bulkDiscount) return;
    setBulkLoading(true);
    try {
      const pct = parseFloat(bulkDiscount) / 100;
      for (const pid of selectedProducts) {
        const prod = products?.find(p => p?.id === pid);
        if (!prod) continue;
        const salePrice = parseFloat((prod?.price * (1 - pct))?.toFixed(2));
        await supabase?.from('retail_products')?.update({ sale_price: salePrice })?.eq('id', pid);
        await supabase?.from('price_history')?.insert({ product_id: pid, partner_id: partnerId, old_price: prod?.sale_price, new_price: salePrice, change_reason: `Bulk ${bulkDiscount}% discount` });
      }
      setMsg({ type: 'success', text: `Applied ${bulkDiscount}% discount to ${selectedProducts?.length} products` });
      setSelectedProducts([]); setBulkDiscount('');
      fetchData();
    } catch (e) { setMsg({ type: 'error', text: 'Failed to apply discount' }); } finally { setBulkLoading(false); }
  };

  const handleCreatePromo = async (e) => {
    e?.preventDefault();
    setPromoLoading(true);
    try {
      const { error } = await supabase?.from('promotions')?.insert({ ...promoForm, partner_id: partnerId, discount_percent: parseFloat(promoForm?.discount_percent), is_active: true });
      if (error) { setMsg({ type: 'error', text: error?.message }); return; }
      setMsg({ type: 'success', text: 'Promotion created!' });
      setShowPromoForm(false);
      setPromoForm({ product_id: '', promotion_name: '', discount_percent: '', start_date: '', end_date: '' });
      fetchData();
    } catch (e) { setMsg({ type: 'error', text: 'Failed to create promotion' }); } finally { setPromoLoading(false); }
  };

  const togglePromo = async (promo) => {
    await supabase?.from('promotions')?.update({ is_active: !promo?.is_active })?.eq('id', promo?.id);
    setPromotions(p => p?.map(x => x?.id === promo?.id ? { ...x, is_active: !x?.is_active } : x));
  };

  const inputStyle = { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' };

  const productHistory = (pid) => priceHistory?.filter(h => h?.product_id === pid);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Pricing Management</h2>
        <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Manage prices, promotions, and discounts</p>
      </div>

      {msg?.text && (
        <div className="px-4 py-2.5 rounded-xl text-sm flex items-center gap-2" style={{ background: msg?.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${msg?.type === 'success' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`, color: msg?.type === 'success' ? '#10B981' : '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>
          {msg?.text}
          <button onClick={() => setMsg({ type: '', text: '' })} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {/* Bulk Discount */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(123,47,190,0.15)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Percent size={16} style={{ color: '#7B2FBE' }} />
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Bulk Discount</h3>
          {selectedProducts?.length > 0 && (
            <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,229,255,0.1)', color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>{selectedProducts?.length} selected</span>
          )}
        </div>
        <div className="flex gap-3 items-end">
          <div className="w-40">
            <label className="block text-xs mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Discount %</label>
            <input type="number" value={bulkDiscount} onChange={e => setBulkDiscount(e?.target?.value)} placeholder="e.g. 15" min="1" max="90"
              className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
          </div>
          <button onClick={handleBulkDiscount} disabled={!selectedProducts?.length || !bulkDiscount || bulkLoading}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all"
            style={{ background: selectedProducts?.length && bulkDiscount ? 'linear-gradient(135deg, #7B2FBE, #00E5FF)' : 'rgba(255,255,255,0.05)', fontFamily: 'DM Sans, sans-serif', opacity: !selectedProducts?.length || !bulkDiscount ? 0.5 : 1 }}>
            {bulkLoading ? 'Applying...' : 'Apply Discount'}
          </button>
        </div>
        <p className="text-xs mt-2" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Select products in the table below, then apply bulk discount</p>
      </div>

      {/* Per-Product Pricing Editor */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,229,255,0.08)' }}>
        <div className="px-5 py-3 flex items-center gap-2" style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(0,229,255,0.08)' }}>
          <DollarSign size={15} style={{ color: '#00E5FF' }} />
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Product Pricing Editor</h3>
        </div>
        {loading ? (
          <div className="p-5 space-y-3">{[...Array(4)]?.map((_, i) => <div key={i} className="h-14 rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.03)' }} />)}</div>
        ) : products?.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No products found. Add products in the catalog first.</p>
        ) : (
          <div>
            {products?.map(p => (
              <div key={p?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <div className="flex items-center gap-3 px-5 py-3">
                  <input type="checkbox" checked={selectedProducts?.includes(p?.id)}
                    onChange={e => setSelectedProducts(prev => e?.target?.checked ? [...prev, p?.id] : prev?.filter(x => x !== p?.id))}
                    className="w-4 h-4 rounded" style={{ accentColor: '#00E5FF' }} />
                  <span className="flex-1 text-sm text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{p?.product_name}</span>
                  <span className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{p?.category}</span>
                  <span className="text-sm font-semibold" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>${parseFloat(p?.price || 0)?.toFixed(2)}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setExpandedProduct(expandedProduct === p?.id ? null : p?.id)} style={{ color: '#64748B' }}>
                      {expandedProduct === p?.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    <button
                      onClick={() => setExpandedHistory(expandedHistory === p?.id ? null : p?.id)}
                      className="text-xs px-2 py-1 rounded-lg"
                      style={{ background: 'rgba(0,229,255,0.08)', color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}
                    >
                      History ({productHistory(p?.id)?.length})
                    </button>
                  </div>
                </div>

                {expandedProduct === p?.id && (
                  <div className="px-5 pb-4" style={{ background: 'rgba(0,0,0,0.15)' }}>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      {[['Original Price', 'price'], ['Sale Price', 'sale_price'], ['Promo Price', 'promotional_price']]?.map(([label, field]) => (
                        <div key={field}>
                          <label className="block text-xs mb-1" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{label}</label>
                          <input type="number" step="0.01"
                            defaultValue={p?.[field] || ''}
                            placeholder="0.00"
                            onChange={e => setPriceEdits(prev => ({ ...prev, [p?.id]: { ...prev?.[p?.id], [field]: e?.target?.value } }))}
                            className="w-full px-3 py-2 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => handleSaveRow(p?.id)}
                      disabled={savingRow === p?.id}
                      className="px-4 py-2 rounded-xl text-xs font-semibold text-white"
                      style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {savingRow === p?.id ? 'Saving...' : 'Save Prices'}
                    </button>
                  </div>
                )}

                {expandedHistory === p?.id && (
                  <div className="px-5 pb-4" style={{ background: 'rgba(0,0,0,0.1)' }}>
                    <p className="text-xs font-medium mb-2" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Price History</p>
                    {productHistory(p?.id)?.length === 0 ? (
                      <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>No price changes recorded</p>
                    ) : (
                      <div className="space-y-1.5">
                        {productHistory(p?.id)?.map(h => (
                          <div key={h?.id} className="flex items-center gap-3 text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)' }}>
                            <span style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>${parseFloat(h?.old_price || 0)?.toFixed(2)}</span>
                            <span style={{ color: '#64748B' }}>→</span>
                            <span style={{ color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>${parseFloat(h?.new_price || 0)?.toFixed(2)}</span>
                            <span className="flex-1" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>{h?.change_reason}</span>
                            <span style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>{h?.changed_at ? new Date(h?.changed_at)?.toLocaleDateString() : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Active Promotions */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(245,158,11,0.12)' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Tag size={16} style={{ color: '#F59E0B' }} />
            <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Active Promotions</h3>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B', fontFamily: 'DM Sans, sans-serif' }}>{promotions?.filter(p => p?.is_active)?.length} active</span>
          </div>
          <button onClick={() => setShowPromoForm(s => !s)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', color: '#F59E0B', fontFamily: 'DM Sans, sans-serif' }}>
            <Plus size={13} /> New Promotion
          </button>
        </div>

        {showPromoForm && (
          <form onSubmit={handleCreatePromo} className="mb-4 p-4 rounded-xl space-y-3" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(245,158,11,0.1)' }}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Promotion Name</label>
                <input value={promoForm?.promotion_name} onChange={e => setPromoForm(f => ({ ...f, promotion_name: e?.target?.value }))} required placeholder="Summer Sale"
                  className="w-full px-3 py-2 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Product</label>
                <select value={promoForm?.product_id} onChange={e => setPromoForm(f => ({ ...f, product_id: e?.target?.value }))} required
                  className="w-full px-3 py-2 rounded-xl text-sm text-white outline-none" style={inputStyle}>
                  <option value="" style={{ background: '#0D1B2A' }}>Select product</option>
                  {products?.map(p => <option key={p?.id} value={p?.id} style={{ background: '#0D1B2A' }}>{p?.product_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Discount %</label>
                <input type="number" value={promoForm?.discount_percent} onChange={e => setPromoForm(f => ({ ...f, discount_percent: e?.target?.value }))} required placeholder="10"
                  className="w-full px-3 py-2 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Start Date</label>
                <input type="datetime-local" value={promoForm?.start_date} onChange={e => setPromoForm(f => ({ ...f, start_date: e?.target?.value }))}
                  className="w-full px-3 py-2 rounded-xl text-sm text-white outline-none" style={{ ...inputStyle, colorScheme: 'dark' }} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs mb-1" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>End Date</label>
                <input type="datetime-local" value={promoForm?.end_date} onChange={e => setPromoForm(f => ({ ...f, end_date: e?.target?.value }))}
                  className="w-full px-3 py-2 rounded-xl text-sm text-white outline-none" style={{ ...inputStyle, colorScheme: 'dark' }} />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowPromoForm(false)} className="px-4 py-2 rounded-xl text-xs" style={{ background: 'rgba(255,255,255,0.05)', color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Cancel</button>
              <button type="submit" disabled={promoLoading} className="px-4 py-2 rounded-xl text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, #F59E0B, #EF4444)', fontFamily: 'DM Sans, sans-serif' }}>
                {promoLoading ? 'Creating...' : 'Create Promotion'}
              </button>
            </div>
          </form>
        )}

        {promotions?.length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No promotions yet. Create your first promotion!</p>
        ) : (
          <div className="space-y-2">
            {promotions?.map(promo => (
              <div key={promo?.id} className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.04)' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate" style={{ fontFamily: 'DM Sans, sans-serif' }}>{promo?.promotion_name}</p>
                  <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{promo?.retail_products?.product_name} · {promo?.discount_percent}% off</p>
                </div>
                {promo?.end_date && <CountdownTimer endDate={promo?.end_date} />}
                <button onClick={() => togglePromo(promo)}
                  className="px-3 py-1 rounded-lg text-xs font-medium flex-shrink-0"
                  style={{ background: promo?.is_active ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.15)', color: promo?.is_active ? '#10B981' : '#64748B', fontFamily: 'DM Sans, sans-serif' }}>
                  {promo?.is_active ? 'Active' : 'Paused'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Price History Log */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Clock size={16} style={{ color: '#00E5FF' }} />
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Recent Price Changes</h3>
        </div>
        {priceHistory?.length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No price changes recorded yet</p>
        ) : (
          <div className="space-y-2">
            {priceHistory?.slice(0, 10)?.map(h => (
              <div key={h?.id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl" style={{ background: 'rgba(0,0,0,0.2)' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate" style={{ fontFamily: 'DM Sans, sans-serif' }}>{h?.retail_products?.product_name || 'Product'}</p>
                  <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{h?.change_reason}</p>
                </div>
                <div className="flex items-center gap-2 text-xs flex-shrink-0">
                  <span style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>${parseFloat(h?.old_price || 0)?.toFixed(2)}</span>
                  <span style={{ color: '#64748B' }}>→</span>
                  <span style={{ color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>${parseFloat(h?.new_price || 0)?.toFixed(2)}</span>
                </div>
                <span className="text-xs flex-shrink-0" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>{h?.changed_at ? new Date(h?.changed_at)?.toLocaleDateString() : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
